/* eslint-disable no-restricted-globals */
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CancelClose, ErrorWarningCircle } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import {
  formatCardNumber,
  formatChargelabAcceptedCharacters,
  formatPercent,
} from '../Vehicles/utils';
import { Icon } from './Icon.component';
import { Label, LabelType } from './Label.component';
import {
  isValidURL,
  validateCurrency,
  validateDisplayId,
  validateDomainName,
  validateEmail,
  validateOcppId,
} from './modal/utils';
import { ColorType } from './shared/Colors.g';

export enum ValidationType {
  NONE,
  NOT_EMPTY,
  NUMBER,
  POSITIVE_NUMBER,
  EMAIL,
  HOURS_12,
  MINS,
  CURRENCY,
  CURRENCY_ZERO_ALLOWED,
  DOMAIN_NAME,
  GEO_LAT,
  GEO_LON,
  PERCENT,
  DURATION,
  IDLE_FEE,
  DISPLAY_ID,
  OCPP_ID,
  LINK,
  POSITIVE_NUMBER_LESS_THAN_100,
  DAY_OF_MONTH,
}

export enum InputType {
  TEXT,
  NUMBER,
  POSITIVE_NUMBER,
  FLOAT_NUMBER,
  VIN, // IN Input can only accept alphanumeric characters O, I, and Q are never present Special characters should not be enterable
  RFID,
  DATE,
  PERCENT,
  GEOLOCATION,
  DOMAIN,
  /**
   * https://chargelab.atlassian.net/wiki/spaces/PROD/pages/1575452675/Style+guide#Accepted-characters
   * Upper and lowercase A-Z
   * All numbers: 0-9
   * Special characters: ! @ # $ & + - ( ) ? , . : ; " '/
   */
  CHARGELAB_ACCEPTED_CHARACTER,
  CURRENCY_INPUT,
}

interface InputProps {
  label?: string;
  labelType?: LabelType;
  labelTypeColor?: ColorType;
  secondLabel?: string;
  secondLabelType?: LabelType;
  secondLabelColor?: ColorType;
  icon?: any;
  iconAlt?: string;
  iconSize?: { width: number; height: number };
  iconColor?: ColorType;
  suffixIcon?: any;
  suffixIconClassName?: string;
  suffixIconSize?: { width: number; height: number };
  errorLabel?: string;
  props?: any;
  placeholder?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  validationType?: ValidationType | ValidationType[];
  onValidation?: Function;
  showErrorIcon?: boolean;
  defaultValue?: any | undefined;
  inputType?: InputType;
  minLen?: string;
  maxLen?: string;
  showFoundResult?: boolean;
  foundResult?: any;
  onCancelClick?: Function;
  iconClassName?: string;
  inputIconPadding?: string;
  validationQueryResult?: boolean;
  hasError?: boolean;
  onClickIcon?: Function;
  width?: string;
  height?: string;
  showOutline?: boolean;
  disabled?: boolean;
  onFocus?: Function;
  onBlur?: Function;
  onClickSuffix?: Function;
  inputId?: string;
  wrapperClassName?: string;
  // TODO: This should be a mandatory field going forward for automated test
  // Keeping it optional until we write all the test
  dataTestId?: string;
  companyCurrency?: string;
  options?: Array<any> | undefined;
  onEnterKeyPress?: Function;
  invalidErrorClass?: string;
  inputClass?: string;
  inputTitle?: string;
}

export const FormInput = ({
  width = 'max-content',
  height = '40px',
  label = '',
  labelType = LabelType.LABEL_M,
  labelTypeColor = ColorType.BLACK,
  secondLabel = '',
  secondLabelType = LabelType.LABEL_M_MEDIUM,
  secondLabelColor = ColorType.GREY6,
  errorLabel: error = '',
  icon,
  iconAlt = '',
  suffixIcon,
  suffixIconClassName = '',
  placeholder = '',
  onChange,
  validationType = ValidationType.NONE,
  onValidation,
  showErrorIcon = false,
  defaultValue = '',
  inputType = InputType.TEXT,
  minLen,
  maxLen = '255',
  props,
  showFoundResult = false,
  foundResult,
  onCancelClick,
  iconClassName = 'pl-2',
  inputIconPadding = 'pl-7',
  iconSize = IconSize.SIZE_16x16,
  iconColor,
  suffixIconSize = IconSize.SIZE_16x16,
  validationQueryResult,
  hasError,
  onClickIcon,
  showOutline = true,
  disabled = false,
  onFocus = () => {},
  onBlur = () => {},
  onClickSuffix = () => null,
  inputId = '',
  wrapperClassName = '',
  dataTestId,
  companyCurrency,
  options = [],
  onEnterKeyPress,
  invalidErrorClass = 'focus:outline-negative border-2 border-negative',

  inputClass = '',
  inputTitle = '',
}: InputProps) => {
  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const [isValid, setIsValid] = useState(true);
  const computedIsValid = hasError === true ? false : isValid;

  const getIsValid = useCallback(
    (val: any) => {
      if (minLen && val.length < Number(minLen)) {
        return false;
      }

      if (validationQueryResult) {
        return false;
      }
      if (onValidation) {
        return onValidation(val);
      }

      if (validationType) {
        const validators = Array.isArray(validationType) ? validationType : [validationType];

        return validators.every((vType) => {
          switch (vType) {
            case ValidationType.GEO_LAT:
              return Number(val) >= -90 && Number(val) <= 90;
            case ValidationType.GEO_LON:
              return Number(val) >= -180 && Number(val) <= 180;
            case ValidationType.DURATION:
              return !_.isEmpty(val) && Number(val) > 0;
            case ValidationType.NOT_EMPTY:
              return !_.isEmpty(val);
            case ValidationType.NUMBER:
              return !_.isEmpty(val) && !isNaN(val);
            case ValidationType.PERCENT:
              return val >= 0 && val <= 100;
            case ValidationType.POSITIVE_NUMBER:
              return !_.isEmpty(val) && !isNaN(val) && Number(val) > 0;
            case ValidationType.POSITIVE_NUMBER_LESS_THAN_100:
              return (
                !_.isEmpty(val) &&
                !isNaN(val) &&
                Number.isInteger(Number(val)) &&
                Number(val) > 0 &&
                Number(val) < 100
              );
            case ValidationType.DAY_OF_MONTH:
              return (
                !_.isEmpty(val) &&
                !isNaN(val) &&
                Number.isInteger(Number(val)) &&
                Number(val) > 0 &&
                Number(val) < 32
              );
            case ValidationType.EMAIL:
              return validateEmail(val);
            case ValidationType.HOURS_12:
              return Number.isInteger(Number(val)) && val >= 1 && val <= 12;
            case ValidationType.MINS:
              if (Number(val) === 0) {
                return val === '0' || val === '00';
              }
              return Number.isInteger(Number(val)) && val >= 0 && val < 60;
            case ValidationType.CURRENCY:
              return validateCurrency(val);
            case ValidationType.CURRENCY_ZERO_ALLOWED:
              return validateCurrency(val, false);
            case ValidationType.DOMAIN_NAME:
              return validateDomainName(val);
            case ValidationType.DISPLAY_ID:
              return validateDisplayId(val);
            case ValidationType.OCPP_ID:
              return validateOcppId(val);
            case ValidationType.LINK:
              return isValidURL(val);
            default:
              return true;
          }
        });
      }
      return true;
    },
    [minLen, onValidation, validationType, validationQueryResult],
  );

  useEffect(() => {
    if (!_.isEmpty(value) || validationQueryResult) {
      setIsValid(getIsValid(value));
    }
  }, [value, validationQueryResult, getIsValid]);

  useEffect(() => {
    if (validationQueryResult !== undefined && !_.isEmpty(value)) {
      setIsValid(!validationQueryResult);
    }
  }, [validationQueryResult, value]);

  const formatInputCurrency = (inputValue: any) => {
    // Null or empty check
    if (inputValue === null || inputValue.trim() === '') {
      return '';
    }

    if (inputValue.startsWith('.')) {
      inputValue = `0${inputValue}`;
    }

    // Ensure only two decimal places
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      if (parts[1].length > 2) {
        return `${parts[0]}.${parts[1].substring(0, 2)}`;
      }
    }
    return inputValue;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.validity.valid) {
      return;
    }
    let newValue = event.target.value;
    switch (inputType) {
      case InputType.PERCENT:
        newValue = formatPercent(event.target.value);
        break;
      case InputType.RFID:
        newValue = formatCardNumber(event.target.value);
        break;
      case InputType.DOMAIN:
        if (event.target.value.includes('@')) break;
        newValue = event.target.value;
        break;
      case InputType.CHARGELAB_ACCEPTED_CHARACTER:
        newValue = formatChargelabAcceptedCharacters(event.target.value);
        break;
      case InputType.CURRENCY_INPUT:
        newValue = formatInputCurrency(event.target.value);
        break;
      default:
        newValue = event.target.value;
        break;
    }
    setIsValid(getIsValid(newValue));
    setValue(newValue);
    onChange &&
      onChange({
        ...event,
        target: {
          ...event.target,
          value: newValue,
        },
      });
  };

  const inputPattern = useMemo(() => {
    switch (inputType) {
      case InputType.GEOLOCATION:
        return '[+|-]?[0-9.]*';
      case InputType.NUMBER:
        return '[0-9]+([,.][0-9]+)?';
      case InputType.POSITIVE_NUMBER:
        return '^[0-9]+$';
      case InputType.FLOAT_NUMBER:
      case InputType.PERCENT:
      case InputType.CURRENCY_INPUT:
        return '[0-9.]*';
      case InputType.VIN:
        return '^(?:(?![OIQoiq])[a-zA-Z0-9])+$';
      case InputType.RFID:
        return '[a-zA-Z0-9 ]*';
      case InputType.DATE:
        return '[0-9/]*';
      default:
        return undefined;
    }
  }, [inputType]);

  const handleOnClick = () => {
    onClickIcon && onClickIcon();
  };

  // eslint-disable-next-line react/prop-types
  const hasStep = props?.step !== undefined;
  // eslint-disable-next-line react/prop-types
  const stepValue = hasStep ? Number(props.step) : 1;

  const handleIncrement = () => {
    const currentValue = Number(value) || 0;
    const newValue = currentValue + stepValue;
    const fakeEvent = {
      target: {
        value: String(newValue),
        validity: { valid: true },
      },
    };
    handleInputChange(fakeEvent as any);
  };

  const handleDecrement = () => {
    const currentValue = Number(value) || 0;
    const newValue = Math.max(0, currentValue - stepValue);
    const fakeEvent = {
      target: {
        value: String(newValue),
        validity: { valid: true },
      },
    };
    handleInputChange(fakeEvent as any);
  };

  return (
    <>
      {inputType === InputType.NUMBER && hasStep && (
        <style>
          {`
          /* Hide native spinner buttons */
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            appearance: none;
            margin: 0;
          }

          input[type="number"] {
            -webkit-appearance: textfield;
            -moz-appearance: textfield;
          }
        `}
        </style>
      )}
      <div className='block' style={{ width }}>
        {label && (
          <div className='flex flex-row pb-2 gap-2 items-baseline'>
            <Label type={labelType} color={labelTypeColor} text={label} labelHtmlFor={inputId} />
            {secondLabel && (
              <Label type={secondLabelType} color={secondLabelColor} text={secondLabel} />
            )}
          </div>
        )}

        <div className={`relative ${wrapperClassName}`}>
          {icon && (
            <span className={`absolute inset-y-0 left-0 flex items-center ${iconClassName}`}>
              <Icon
                src={icon}
                onClick={handleOnClick}
                size={iconSize}
                color={iconColor}
                alt={iconAlt}
              />
            </span>
          )}
          <input
            type={inputType === InputType.NUMBER && hasStep ? 'number' : undefined}
            pattern={inputPattern}
            id={inputId}
            placeholder={placeholder}
            autoComplete='off'
            className={`
          placeholder:text-grey4 pt-2.5 pb-2.5 rounded text-sm font-inter text-black bg-grey1 hover:bg-grey2 ${inputClass}
          ${suffixIcon ? 'pr-7' : inputType === InputType.NUMBER && hasStep ? 'pr-[30px]' : 'pr-3'}
          ${
            showOutline
              ? computedIsValid
                ? 'focus:outline-black'
                : invalidErrorClass
              : computedIsValid
              ? 'focus:outline-none'
              : invalidErrorClass
          }
          ${icon ? inputIconPadding : 'pl-3'}
           `}
            onChange={handleInputChange}
            value={value}
            maxLength={maxLen}
            style={{ width, height }}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            data-testid={dataTestId}
            onKeyDown={onEnterKeyPress}
            title={inputTitle}
            aria-label={inputId}
            {...props}
          />
          {inputType === InputType.NUMBER && hasStep && !disabled && (
            <div className='absolute inset-y-0 right-0 flex flex-col items-center justify-center pr-[5px] pointer-events-none'>
              <button
                type='button'
                onClick={handleIncrement}
                className='flex items-center justify-center cursor-pointer hover:opacity-70 pointer-events-auto'
                aria-label='Increment'
                tabIndex={-1}
                style={{ width: '20px', height: '10px' }}
              >
                <Icon src={ArrowUp} size={{ width: 9.31, height: 5.49 }} color={ColorType.GREY6} />
              </button>
              <button
                type='button'
                onClick={handleDecrement}
                className='flex items-center justify-center cursor-pointer hover:opacity-70 pointer-events-auto'
                aria-label='Decrement'
                tabIndex={-1}
                style={{ width: '20px', height: '10px' }}
              >
                <Icon
                  src={ArrowDown}
                  size={{ width: 9.31, height: 5.49 }}
                  color={ColorType.GREY6}
                />
              </button>
            </div>
          )}
          {suffixIcon && !disabled && (
            <span
              className={`absolute inset-y-0  ${
                !computedIsValid && showErrorIcon
                  ? 'right-3 flex items-center mr-[30px]'
                  : 'right-0 flex items-center mr-[10px]'
              } ${suffixIconClassName}`}
            >
              <Icon
                size={suffixIconSize}
                key={suffixIcon}
                src={suffixIcon}
                onClick={onClickSuffix}
              />
            </span>
          )}
          {!computedIsValid && showErrorIcon && (
            <span className='absolute inset-y-0 right-0 flex items-center mr-[15px]'>
              <Icon
                src={ErrorWarningCircle}
                size={IconSize.SIZE_20x20}
                alt='icon_alt_error'
                isTranslationOn
              />
            </span>
          )}
          {showFoundResult && (
            <div className='absolute items-center right-1 top-2.5 opacity-50'>
              <div className='flex flex-row items-center gap-4'>
                <div className='text-sm text-grey5 font-normal'>{foundResult}</div>
                <Label
                  type={LabelType.LABEL_M}
                  text=''
                  icon={CancelClose}
                  onClick={onCancelClick}
                />
              </div>
            </div>
          )}
        </div>
        {!computedIsValid && error && (
          <Label
            type={LabelType.BODY3}
            color={ColorType.NEGATIVE}
            className='pt-1 block'
            text={error}
          />
        )}
      </div>
    </>
  );
};
